import { FC, RefObject, useRef } from 'react';

import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Badge,
	Box,
	Button,
	Divider,
	HStack,
	Spinner,
	Text,
	useDisclosure,
	useToast,
	VStack,
} from '@bonusly/design-system';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { useBizyEverboarding, useBizyResetEverboardingMutation } from '../../../../queries/bizy-everboarding';
import { BizyEverboardingStatus } from '../../../../schemas/bizy-everboarding';

// ─── Status display helpers ──────────────────────────────────────────────────

const STATUS_CONFIG: Record<BizyEverboardingStatus, { label: string; colorScheme: string }> = {
	not_started: { label: 'Not Started', colorScheme: 'gray' },
	in_progress: { label: 'In Progress', colorScheme: 'blue' },
	completed: { label: 'Completed', colorScheme: 'green' },
	dismissed: { label: 'Dismissed', colorScheme: 'orange' },
};

function formatTimestamp(date: Date | null): string {
	if (!date) return '—';
	return date.toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
}

// ─── Timestamp Row ───────────────────────────────────────────────────────────

const TimestampRow: FC<{ label: string; date: Date | null }> = ({ label, date }) => (
	<HStack justify="space-between" spacing={2}>
		<Text fontSize="xs" color="text.softer">
			{label}
		</Text>
		<Text fontSize="xs" color="text.soft" fontFamily="mono">
			{formatTimestamp(date)}
		</Text>
	</HStack>
);

// ─── Everboarding Section ────────────────────────────────────────────────────

export const EverboardingSection = () => {
	const isEnabled = useFeatureFlag('bizy-everboarding');
	const { data: everboarding, isLoading } = useBizyEverboarding(isEnabled);
	const resetMutation = useBizyResetEverboardingMutation();
	const toast = useToast();

	const confirmDialog = useDisclosure();
	const cancelRef = useRef<HTMLButtonElement>(null);

	if (!isEnabled) return null;

	const handleReset = () => {
		resetMutation.mutate(undefined, {
			onSuccess: () => {
				confirmDialog.onClose();
				toast({ title: 'Everboarding has been reset', status: 'success', duration: 3000 });
			},
			onError: () => {
				confirmDialog.onClose();
				toast({ title: 'Failed to reset everboarding', status: 'error', duration: 3000 });
			},
		});
	};

	const statusConfig = everboarding ? STATUS_CONFIG[everboarding.status] : null;
	const canReset = everboarding && everboarding.status !== 'not_started';

	return (
		<>
			<Divider />
			<Box>
				<Text fontSize="sm" fontWeight="semibold" color="text.default" mb={2}>
					Everboarding
				</Text>
				<Text fontSize="xs" color="text.softer" mb={3}>
					Your onboarding progress with Bizy. Reset to start the guided experience again.
				</Text>

				{isLoading ? (
					<HStack justify="center" py={4}>
						<Spinner size="sm" />
					</HStack>
				) : everboarding ? (
					<VStack spacing={3} align="stretch">
						<Box bg="secondary.bgAlpha" borderRadius="md" px={3} py={2}>
							<HStack spacing={2} mb={2}>
								<Text fontSize="xs" fontWeight="medium" color="text.soft">
									Status
								</Text>
								{statusConfig && (
									<Badge colorScheme={statusConfig.colorScheme} fontSize="xs">
										{statusConfig.label}
									</Badge>
								)}
							</HStack>

							<VStack spacing={1} align="stretch">
								<TimestampRow label="Started at" date={everboarding.createdAt} />
								<TimestampRow label="Completed at" date={everboarding.completedAt} />
								<TimestampRow label="Dismissed at" date={everboarding.dismissedAt} />
							</VStack>
						</Box>

						<Button size="sm" variant="outline" colorScheme="secondary" isDisabled={!canReset} onClick={confirmDialog.onOpen}>
							Reset Everboarding
						</Button>
					</VStack>
				) : (
					<Text fontSize="sm" color="text.softer">
						Unable to load everboarding status.
					</Text>
				)}

				<AlertDialog leastDestructiveRef={cancelRef as RefObject<HTMLButtonElement>} isOpen={confirmDialog.isOpen} onClose={confirmDialog.onClose} isCentered>
					<AlertDialogOverlay>
						<AlertDialogContent>
							<AlertDialogHeader fontSize="lg" fontWeight="bold">
								Reset Everboarding
							</AlertDialogHeader>
							<AlertDialogCloseButton />
							<AlertDialogBody>
								<Text>This will delete your everboarding progress and you'll go through the guided experience again. Are you sure?</Text>
							</AlertDialogBody>
							<AlertDialogFooter gap={3}>
								<Button ref={cancelRef} variant="outline" colorScheme="secondary" onClick={confirmDialog.onClose}>
									Cancel
								</Button>
								<Button variant="solid" colorScheme="danger" onClick={handleReset} isLoading={resetMutation.isLoading}>
									Reset
								</Button>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialogOverlay>
				</AlertDialog>
			</Box>
		</>
	);
};
